import React from "react";
// import List from "@material-ui/core/List";
// import Divider from "@material-ui/core/Divider";
import { SideDrawerTab, ViewContext } from "context/View";
import { UserContext } from "context/User";
import ConnectionAlert from "components/utils/ConnectionAlert";
import { addressCoverage, contactCoverage } from "utils/coverage";
import { secondChanceLockdown } from "utils/secondChance";

import { Link } from "react-router-dom";
import { List, Divider, styled } from "@mui/material";
import { DeveloperMode, People, PeopleAlt } from "@mui/icons-material";

interface SideDrawerProps {
    handleMobileEvent: (type: string) => void,
}

const SideDrawer = ({ handleMobileEvent }: SideDrawerProps) => {
    const { currentPanel, saveCurrentPanel } = React.useContext(ViewContext);
    const { currentUser, internalUser } = React.useContext(UserContext);


    const DrawerHeader = styled('div')(({ theme }) => ({
        display: "flex",
        alignItems: "center",
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
        justifyContent: 'flex-end',
    }));

    // Front-end gating of the side menu while a reseller is still onboarding (not yet
    // approved). Signals mirror the wizard exactly so the two can't disagree:
    //   • before step 3 (info form + certificate done): My Company + everything below is locked
    //   • at step 3: My Company unlocks so they can add their billing address/contact
    //   • once account setup is complete: Products / Orders / Documents open up
    // Approved and internal users are never gated.
    const company = currentUser?.company;
    const onboarding = !!company && !company.approved && !internalUser && !!company.approvalStatus;
    const step12Done =
        !!company?.approvalStatus?.infoFormComplete && !!company?.approvalStatus?.certificateUploaded;
    // Real coverage only — exiting the guided tour (accountTourDone) must not count
    // as finishing account setup when the required data isn't there. Also requires a
    // designated default billing address + contact, matching the wizard.
    const accountSetupDone =
        addressCoverage(company?.addresses).complete && contactCoverage(company?.contacts).complete &&
        !!company?.defaultBillingAddress && !!company?.defaultBillingContact;
    // Bucket A inside its one-time reactivation window: Products / Orders / Documents
    // stay locked for the WHOLE grace period, not just until account setup — they owe
    // a signature, not a purchase, so open catalog access removes the only incentive.
    const secondChanceLocked = secondChanceLockdown(company);
    const lockCompany = onboarding && !step12Done;        // My Company until steps 1 & 2 are done
    const lockTransact = secondChanceLocked || (onboarding && !accountSetupDone);  // Products / Orders / Documents until account setup is done
    const transactLockedReason = (fallback: string) =>
        secondChanceLocked ? "Sign your reseller agreement to restore access." : fallback;

    const navButton = ({ to, panel, icon, label, locked, lockedReason, onClickExtra, dataTour }: {
        to: string; panel: SideDrawerTab; icon: React.ReactNode; label: string;
        locked?: boolean; lockedReason?: string; onClickExtra?: () => void; dataTour?: string;
    }) => {
        const inner = (
            <div
                data-tour={dataTour}
                className={currentPanel === panel ? "sidenav-button active" : "sidenav-button"}
                title={locked ? lockedReason : undefined}
                onClick={locked ? undefined : () => {
                    if (onClickExtra) onClickExtra();
                    saveCurrentPanel(panel);
                    handleMobileEvent("mobile");
                }}
            >
                {icon}
                <p>{label}</p>
            </div>
        );
        return locked
            ? <span className="sidenav-a" style={{ cursor: "not-allowed", opacity: 0.4 }}>{inner}</span>
            : <Link className="sidenav-a" to={to}>{inner}</Link>;
    };

    return (
        <div style={{
                minWidth: 100,
                width: "100%",
                backgroundColor: "#000",
                color: "#828282",
            }}>
            <Link to="/">
                <DrawerHeader
                    onClick={() => {
                        saveCurrentPanel(SideDrawerTab.home);
                        handleMobileEvent("mobile");
                    }}
                    className="badge"
                    style={{
                        position: "relative",
                        left: 0,
                        right: 0,
                        margin: "0 auto",
                    }}
                >
                    <ConnectionAlert />
                </DrawerHeader>
            </Link>

            <List>
                <Link style={{ marginTop: 40 }} className="sidenav-a" to="/">
                    <div
                        className={
                            currentPanel === SideDrawerTab.home
                                ? "sidenav-button active"
                                : "sidenav-button"
                        }
                        onClick={() => {
                            saveCurrentPanel(SideDrawerTab.home);
                            handleMobileEvent("mobile");
                        }}
                        key="Home"
                    >
                        <i className="far fa-home"></i>
                        <p>Home</p>
                    </div>
                </Link>

                {navButton({
                    to: "/products",
                    panel: SideDrawerTab.products,
                    icon: <i className="far fa-dolly-flatbed-alt"></i>,
                    label: "Products",
                    locked: lockTransact,
                    lockedReason: transactLockedReason("Finish account setup to unlock products."),
                    onClickExtra: () => localStorage.removeItem("last-search"),
                })}

                {navButton({
                    to: "/order-history",
                    panel: SideDrawerTab.orders,
                    icon: <i className="far fa-receipt"></i>,
                    label: "Orders",
                    locked: lockTransact,
                    lockedReason: transactLockedReason("Finish account setup to unlock orders."),
                })}

                {/* <Link className="sidenav-a" to="/files">
          <div
            className={
              currentPanel === "files"
                ? "sidenav-button active"
                : "sidenav-button"
            }
            onClick={() => {
              saveCurrentPanel("files");
              handleMobileEvent("mobile");
            }}
            key="Files"
          >
            <i className="fas fa-folders"></i>
            <p>My Files</p>
          </div>
        </Link> */}

                {navButton({
                    to: "/company",
                    panel: SideDrawerTab.company,
                    icon: <i className="far fa-building"></i>,
                    label: "My Company",
                    locked: lockCompany,
                    lockedReason: "Complete the info form and upload your certificate first.",
                    dataTour: "nav-company",
                })}

                {currentUser &&
                    internalUser && (
                    <Link className="sidenav-a" to="/admin" state={{ startingPage: "landing" }}>
                        <div
                            className={
                                currentPanel === SideDrawerTab.admin
                                    ? "sidenav-button active"
                                    : "sidenav-button"
                            }
                            onClick={() => {
                                saveCurrentPanel(SideDrawerTab.admin);
                                handleMobileEvent("mobile");
                            }}
                            key="Admin"
                        >
                            <i className="far fa-snowflake"></i>
                            <p>Admin</p>
                        </div>
                    </Link>
                )}

                {currentUser &&
                    (internalUser || (currentUser.company && currentUser.company.companyType == "Rep" && currentUser.accountLevel && currentUser.accountLevel.viewOrders)) && (
                    <>
                        <Link className="sidenav-a" to="/interactions">
                            <div
                                className={
                                    currentPanel === SideDrawerTab.interactions
                                        ? "sidenav-button active"
                                        : "sidenav-button"
                                }
                                onClick={() => {
                                    saveCurrentPanel(SideDrawerTab.interactions);
                                    handleMobileEvent("mobile");
                                }}
                                key="Interactions"
                            >
                                <PeopleAlt />
                                <p>Interactions</p>
                            </div>
                        </Link>
                    </>
                )}

                {currentUser &&
                    (internalUser || (currentUser.company && currentUser.company.companyType == "Rep" && currentUser.accountLevel && currentUser.accountLevel.viewOrders)) && (
                    <>
                        <Link className="sidenav-a" to="/pending-orders?demos=true">
                            <div
                                className={
                                    currentPanel === SideDrawerTab.demos
                                        ? "sidenav-button active"
                                        : "sidenav-button"
                                }
                                onClick={() => {
                                    saveCurrentPanel(SideDrawerTab.demos);
                                    handleMobileEvent("mobile");
                                }}
                                key="Demos"
                            >
                                <DeveloperMode />
                                <p>Demos</p>
                            </div>
                        </Link>
                    </>
                )}

                {currentUser &&
                    (internalUser || (currentUser.company && currentUser.company.companyType == "Rep" && currentUser.accountLevel && currentUser.accountLevel.viewOrders)) && (
                    <>
                        <Link className="sidenav-a" to={"/reports" + (!internalUser ? "/sales-by-reseller" : "")}>
                            <div
                                className={
                                    currentPanel === SideDrawerTab.reports
                                        ? "sidenav-button active"
                                        : "sidenav-button"
                                }
                                onClick={() => {
                                    saveCurrentPanel(SideDrawerTab.reports);
                                    handleMobileEvent("mobile");
                                }}
                                key="Reports"
                            >
                                <i className="fas fa-folders"></i>
                                <p>Reports</p>
                            </div>
                        </Link>
                    </>
                )}

                {currentUser && !internalUser && (
                    navButton({
                        to: "/company?tab=documents",
                        panel: SideDrawerTab.documents,
                        icon: <i className="far fa-file-invoice"></i>,
                        label: "Documents",
                        locked: lockTransact,
                        lockedReason: transactLockedReason("Finish account setup to unlock documents."),
                    })
                )}


                <Link className="sidenav-a" to="/help">
                    <div
                        className={
                            currentPanel === SideDrawerTab.help
                                ? "sidenav-button active"
                                : "sidenav-button"
                        }
                        onClick={() => {
                            saveCurrentPanel(SideDrawerTab.help);
                            handleMobileEvent("mobile");
                        }}
                        key="Help"
                    >
                        <i className="far fa-life-ring"></i>
                        <p>Help</p>
                    </div>
                </Link>
            </List>
            <Divider />
        </div>
    );
};

export default SideDrawer;
